/**
 * The whole app, in one router.
 *
 * TWO WORLDS, ONE BUNDLE:
 *
 *   /c/:token   the CUSTOMER surface. No login, no staff token, no app chrome,
 *               its own locale, its own font stack. It is mounted OUTSIDE every
 *               staff provider so a guest's phone never instantiates an auth
 *               context, never touches the staff outbox and can never be handed
 *               a cashier's bearer token by accident.
 *
 *   /*          the STAFF surface. Auth → permissions → offline outbox →
 *               device-mode resolution → one of seven completely different
 *               shells. See `device/mode.ts` for the "one app, many faces"
 *               mechanism the spec requires.
 */

import { Routes, Route, useParams, Navigate } from 'react-router-dom';
import { ConfirmProvider, PermissionProvider, ToastProvider, LoadingState, ErrorState, Button } from '@sonauto/ui';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { LoginScreen } from './auth/LoginScreen';
import { OutboxProvider } from './offline/OutboxProvider';
import { DeviceModeProvider, useDeviceMode } from './device/DeviceModeProvider';
import { ActivationScreen } from './device/ActivationScreen';
import { CashierShell } from './modes/cashier/CashierShell';
import { ServiceShell } from './modes/service/ServiceShell';
import { ProductionShell } from './modes/production/ProductionShell';
import { WarehouseShell } from './modes/warehouse/WarehouseShell';
import { QueueShell } from './modes/queue/QueueShell';
import { ManagerShell } from './modes/manager/ManagerShell';
import { CustomerApp } from './customer/CustomerApp';
import { useState } from 'react';

export function App(): React.JSX.Element {
  return (
    <Routes>
      {/* The guest surface. Deliberately outside every staff provider. */}
      <Route path="/c/:token/*" element={<CustomerRoute />} />
      {/* `/q?t=…` is the URL shape `@sonauto/qr` prints on the slip. */}
      <Route path="/q" element={<QueryTokenRedirect />} />
      <Route path="/*" element={<StaffApp />} />
    </Routes>
  );
}

function CustomerRoute(): React.JSX.Element {
  const { token } = useParams<{ token: string }>();
  if (token === undefined || token === '') {
    return <ErrorState title="Mã QR không hợp lệ" message="Vui lòng quét lại mã QR trên bàn của bạn." />;
  }
  return <CustomerApp token={token} />;
}

/**
 * `@sonauto/qr`'s `buildQrUrl` produces `/q?t=<token>`. Normalise it onto the
 * canonical `/c/:token` route and drop the token from the address bar — it is a
 * bearer credential and has no business sitting in history or in a referrer.
 */
function QueryTokenRedirect(): React.JSX.Element {
  const token = new URLSearchParams(window.location.search).get('t');
  if (token === null || token === '') {
    return <ErrorState title="Mã QR không hợp lệ" message="Vui lòng quét lại mã QR trên bàn của bạn." />;
  }
  return <Navigate to={`/c/${encodeURIComponent(token)}`} replace />;
}

function StaffApp(): React.JSX.Element {
  return (
    <AuthProvider>
      <ToastProvider>
        <ConfirmProvider>
          <StaffGate />
        </ConfirmProvider>
      </ToastProvider>
    </AuthProvider>
  );
}

function StaffGate(): React.JSX.Element {
  const { signedIn, me, loadingMe, meError, reloadMe, signOut } = useAuth();

  if (!signedIn) return <LoginScreen />;
  if (loadingMe && me === null) return <LoadingState label="Đang tải phiên làm việc…" />;
  if (meError !== null && me === null) {
    return (
      <div className="p-6">
        <ErrorState title="Không tải được phiên làm việc" message={meError} onRetry={reloadMe}>
          <Button variant="ghost" size="sm" onClick={() => void signOut()}>
            Đăng xuất
          </Button>
        </ErrorState>
      </div>
    );
  }
  if (me === null) return <LoadingState label="Đang tải phiên làm việc…" />;

  return (
    <PermissionProvider
      value={{
        permissions: new Set(me.permissions),
        permissionsByBranch: Object.fromEntries(
          Object.entries(me.permissionsByBranch).map(([branchId, codes]) => [branchId, new Set(codes)]),
        ),
        activeBranchId: me.session.activeBranchId,
      }}
    >
      <OutboxProvider>
        <DeviceModeProvider>
          <ModeShell />
        </DeviceModeProvider>
      </OutboxProvider>
    </PermissionProvider>
  );
}

/**
 * THE FACE SWITCH.
 *
 * One branch per mode, and nothing else in the app switches on the mode. Adding
 * a mode is: one entry in `DEVICE_MODES`, one case here, one shell.
 */
function ModeShell(): React.JSX.Element {
  const device = useDeviceMode();
  const [activating, setActivating] = useState(false);

  if (activating) return <ActivationScreen onDone={() => setActivating(false)} />;

  switch (device.mode) {
    case 'POS_CASHIER':
      return <CashierShell onActivateDevice={() => setActivating(true)} />;
    case 'SERVICE_TERMINAL':
      return <ServiceShell onActivateDevice={() => setActivating(true)} />;
    case 'KITCHEN_DISPLAY':
    case 'BAR_DISPLAY':
      return <ProductionShell mode={device.mode} onActivateDevice={() => setActivating(true)} />;
    case 'WAREHOUSE_TERMINAL':
      return <WarehouseShell onActivateDevice={() => setActivating(true)} />;
    case 'QUEUE_DISPLAY':
      return <QueueShell onActivateDevice={() => setActivating(true)} />;
    case 'MANAGER_TERMINAL':
      return <ManagerShell onActivateDevice={() => setActivating(true)} />;
    case 'CUSTOMER_ORDER':
      // Unreachable by login — CUSTOMER_ORDER is a URL, not a session. A device
      // row registered as CUSTOMER_ORDER that someone signs into gets told so
      // rather than being silently handed a staff shell.
      return (
        <div className="p-6">
          <ErrorState
            title="Thiết bị này được đăng ký cho khách quét QR"
            message="Chế độ khách chỉ vào được bằng đường dẫn /c/:token in trên phiếu QR, không đăng nhập."
          />
        </div>
      );
  }
}
